"use client";

import { useEffect, useState } from "react";
import Image from "next/image";

/**
 * Echter Infinity-Loop-Marquee – jetzt viewport-aware.
 *
 * Die nahtlose Schleife funktioniert nur, wenn das Gesamt-Content
 * mindestens 2× so breit ist wie der Viewport:
 *
 *     total >= 2 · viewportWidth
 *     ↓ wo                  total = copies · logos.length · itemWidth
 *     copies >= ceil( 2 · viewportWidth / (logos.length · itemWidth) )
 *
 * Ist der Content zu schmal, klafft am Animations-Endpunkt eine Lücke
 * (rechts sichtbar leer, beim Wrap springt es kurz zurück) – exakt der
 * Bug, den wir hier endgültig schließen.
 *
 * Außerdem skalieren wir die Animationsdauer mit dem Content, damit die
 * Lauf-Geschwindigkeit (ca. 80 px/s) unabhängig von Logo-Anzahl und
 * Bildschirm-Breite konstant bleibt – sonst würde sie auf großen
 * Monitoren spürbar schneller wirken.
 *
 * Logos werden mit next/image gerendert (Quellen: `/public/images/logos/`
 * ODER Supabase Storage). Der Supabase-Host ist in next.config.ts unter
 * images.remotePatterns freigeschaltet. Vorteil gegenüber dem früheren
 * rohen <img>: Vercel optimiert + cacht die Storage-Logos am Edge mit
 * langem immutable-Header. Davor lieferte Supabase sie mit
 * `cache-control: no-cache` aus → bei jedem Seitenaufruf Re-Download
 * über eine separate Cross-Origin-Verbindung (das spürbare „mal
 * langsamer"-Laden der zuletzt hinzugefügten Logos).
 */

type Logo = {
  id: string;
  src: string;
  alt: string;
  href: string | null;
};

// w-32 (128 px) + mr-16 (64 px) – muss zu den Tailwind-Klassen im JSX passen.
const ITEM_WIDTH_PX = 128 + 64;
// Wunsch-Lauf-Geschwindigkeit (px/s). Bewusst gemächlich gewählt,
// damit Logos genug Zeit haben, vom Auge erfasst zu werden.
const PIXELS_PER_SECOND = 45;
// Konservativer Fallback: bei SSR/initialem Render kennen wir die
// Viewport-Breite noch nicht. 4K-Breite als sichere Annahme – lieber
// einmal zu viele Kopien als ein sichtbarer Gap.
const SSR_FALLBACK_WIDTH = 3840;

function computeCopies(logoCount: number, viewportWidth: number): number {
  if (logoCount === 0) return 0;
  const setWidth = logoCount * ITEM_WIDTH_PX;
  // Mindestens 2 Kopien (sonst funktioniert das -50%-Loop-Pattern nicht).
  // Aufgerundet auf gerade Zahl, damit -50% genau auf einer Set-Grenze
  // landet → nahtloser Wrap.
  const raw = Math.max(2, Math.ceil((2 * viewportWidth) / setWidth));
  return raw % 2 === 0 ? raw : raw + 1;
}

export function LogoSliderClient({ logos }: { logos: Logo[] }) {
  const [copies, setCopies] = useState(() =>
    computeCopies(logos.length, SSR_FALLBACK_WIDTH),
  );

  useEffect(() => {
    if (logos.length === 0) return;
    const recompute = () => {
      setCopies(computeCopies(logos.length, window.innerWidth));
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [logos.length]);

  if (logos.length === 0) return null;

  // Items so oft wiederholen, wie nötig.
  const repeated: Logo[] = Array.from({ length: copies }, () => logos).flat();
  // Dauer: wir bewegen uns über 50 % der Gesamtbreite. Bei konstanter
  // Geschwindigkeit ergibt das ein größeres Setup → längere Cycle-Time.
  const totalWidth = copies * logos.length * ITEM_WIDTH_PX;
  const durationSeconds = Math.max(
    20,
    Math.round(totalWidth / 2 / PIXELS_PER_SECOND),
  );

  return (
    <section
      className="py-12"
      style={{ backgroundColor: "var(--bg-secondary)" }}
    >
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <p
          className="mb-10 text-center text-sm font-medium uppercase tracking-wider"
          style={{ color: "var(--text-quaternary)" }}
        >
          Unternehmen die uns vertrauen
        </p>
      </div>
      {/* py-3: Platz für das helle Hover-Plättchen im Dark-Mode (box-shadow
          10 px), das sonst oben/unten abgeschnitten würde. */}
      <div className="relative overflow-hidden py-3">
        {/* Fade edges */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24"
          style={{
            background:
              "linear-gradient(to right, var(--bg-secondary), transparent)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24"
          style={{
            background:
              "linear-gradient(to left, var(--bg-secondary), transparent)",
          }}
        />
        <div
          className="animate-marquee flex w-max items-center whitespace-nowrap"
          // Animationsdauer wird hier inline gesetzt, damit die globale
          // Klassen-Definition nicht angefasst werden muss.
          style={{ animationDuration: `${durationSeconds}s` }}
        >
          {repeated.map((logo, i) => {
            const inner = (
              <Image
                src={logo.src}
                alt={logo.alt}
                width={128}
                height={40}
                // h-auto/w-auto: Originale haben unterschiedliche
                // Seitenverhältnisse, object-contain hält sie sauber.
                className="h-auto max-h-10 w-auto object-contain"
                // Logo-Slider ist below-the-fold → lazy (next/image-Default),
                // kein priority/Preload nötig. aria-hidden-Kopien werden vom
                // Browser ohnehin verzögert geladen.
                sizes="128px"
              />
            );
            return (
              <div
                key={`${logo.id}-${i}`}
                // mr-16 (4rem = 64 px) UNIFORM auf jedem Item, auch dem
                // letzten – sonst klafft am Loop-Punkt eine halbe Lücke.
                // aria-hidden ab der 2. Kopie: Screenreader sollen die
                // Logos nicht mehrfach vorlesen.
                aria-hidden={i >= logos.length ? "true" : undefined}
                // dmd-logo-slider-item wird in globals.css je nach Theme
                // gefiltert (grau, Dark-Mode invertiert). Beim Hover werden
                // die Originalfarben gezeigt – im Dark-Mode auf hellem Plättchen.
                className="dmd-logo-slider-item mr-16 flex h-10 w-32 shrink-0 items-center justify-center transition-all duration-300"
              >
                {logo.href ? (
                  <a
                    href={logo.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={logo.alt}
                    className="flex items-center"
                    tabIndex={i >= logos.length ? -1 : 0}
                  >
                    {inner}
                  </a>
                ) : (
                  inner
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
